import { useEffect, useRef } from "react";

/**
 * Safety net for native HTML5 drag state.
 *
 * Every drag-and-drop surface in the app keeps a piece of React state for the
 * duration of a drag (which block is being dragged, which row is a drop
 * target, …) and clears it in `onDragEnd`. That is one event away from a
 * bricked screen: when the browser abandons a drag without dispatching
 * `dragend` to the source element — Chrome does exactly that when the layout
 * shifts under a drag it is still initiating, and it also happens when the
 * source is unmounted mid-drag — the state stays set forever. Every drop zone
 * keeps painting, and the click handlers that guard on "not dragging" stop
 * responding: the page looks alive but ignores every click until a reload.
 *
 * Pass `active` (is a drag currently recorded in React state?) and the same
 * teardown the `onDragEnd` handler runs. While active, the guard watches the
 * document for every sign that the drag is over:
 *
 *  - `dragend` anywhere, not only on the source element;
 *  - Escape, which cancels a drag in every browser;
 *  - a plain pointer move or press. While a native drag is in flight the
 *    browser suppresses pointer/mouse move events and dispatches
 *    `drag`/`dragover` instead, so a pointer event proves that no drag is
 *    running any more — whichever event the browser skipped.
 *
 * The teardown must be idempotent: several of these signals normally arrive
 * for one drag.
 *
 * Deliberately NOT listened for: `drop`. The components' own drop handlers
 * read the drag state and stop propagation, so a document-level listener
 * would race them — the pointer rule above already covers a drop whose
 * `dragend` goes missing.
 */

/**
 * Quiet period since the last drag signal before a pointer event is trusted
 * as proof that the drag ended. It absorbs a pointer event the browser had
 * already queued when the drag began, so a healthy drag is never cancelled
 * out from under the user.
 */
export const DRAG_IDLE_MS = 200;

export function useDragSessionGuard(active: boolean, onDragSessionEnd: () => void): void {
  // Kept in a ref so a fresh inline callback each render doesn't re-attach
  // the listeners, which would restart the quiet period mid-drag.
  const endRef = useRef(onDragSessionEnd);
  endRef.current = onDragSessionEnd;

  useEffect(() => {
    if (!active) return;

    // Arming counts as the first proof the drag is alive; `drag`/`dragover`
    // (which fire continuously while a real drag moves over the document)
    // refresh it.
    let lastDragSignalAt = performance.now();

    const end = () => endRef.current();
    const noteDragAlive = () => {
      lastDragSignalAt = performance.now();
    };
    const onPointer = () => {
      if (performance.now() - lastDragSignalAt >= DRAG_IDLE_MS) end();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") end();
    };

    // Capture phase throughout: a handler that stops propagation (Radix
    // menus, nested drop zones) must not be able to hide the end of a drag.
    const capture = true;
    document.addEventListener("drag", noteDragAlive, capture);
    document.addEventListener("dragover", noteDragAlive, capture);
    document.addEventListener("dragend", end, capture);
    document.addEventListener("pointermove", onPointer, capture);
    document.addEventListener("pointerdown", onPointer, capture);
    document.addEventListener("keydown", onKeyDown, capture);

    return () => {
      document.removeEventListener("drag", noteDragAlive, capture);
      document.removeEventListener("dragover", noteDragAlive, capture);
      document.removeEventListener("dragend", end, capture);
      document.removeEventListener("pointermove", onPointer, capture);
      document.removeEventListener("pointerdown", onPointer, capture);
      document.removeEventListener("keydown", onKeyDown, capture);
    };
  }, [active]);
}
